import React, { useMemo, useState } from 'react';
import Popover from '@mui/material/Popover';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useTranslation } from 'react-i18next';
import { CircleIconButton } from '@/components/ui/CircleIconButton';
import { MonthCalendar } from '@/components/MonthCalendar';
import { useBreakpointDown, useShortViewport } from '@/hooks/useBreakpoint';
import { parseLocalIsoDate, shiftIsoDate, toLocalIsoDate } from '@/utils/localDate';
import styles from './styles.module.scss';

export interface DatePagerProps {
  /** Selected local day, `YYYY-MM-DD`. */
  value: string;
  /** Today's local day, `YYYY-MM-DD`. */
  today: string;
  onChange: (iso: string) => void;
  /** Pager test ids: `${prefix}-prev`, `${prefix}-next`, `${prefix}-label`. */
  testIdPrefix?: string;
  /** Calendar test ids: `${prefix}` (container), `${prefix}-today`, `${prefix}-day-YYYY-MM-DD`. */
  calendarTestIdPrefix?: string;
}

type RelativeDay = 'today' | 'yesterday' | 'tomorrow';

// SwipeableDrawer requires `onOpen`; swipe-to-open is disabled.
const noop = () => {};

/**
 * Day pager: previous / label / next. The label opens a month calendar —
 * a bottom sheet below `tablet` (thumb zone, above BottomNav) and a popover
 * anchored to the label from `tablet` up. Both render through a portal, so
 * `overflow: hidden` ancestors (SwipeableTabContent) cannot clip them.
 */
export const DatePager: React.FC<DatePagerProps> = ({
  value,
  today,
  onChange,
  testIdPrefix = 'matches-date',
  calendarTestIdPrefix = 'matches-calendar',
}) => {
  const { t, i18n } = useTranslation(['league']);
  const locale = i18n.language;
  // Sheet on phones and on short (landscape) viewports, where an anchored
  // popover would not fit under the label.
  const isNarrow = useBreakpointDown('tablet');
  const isShort = useShortViewport();
  const isSheet = isNarrow || isShort;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = anchorEl !== null;

  const valueDate = useMemo(() => parseLocalIsoDate(value), [value]);
  const todayDate = useMemo(() => parseLocalIsoDate(today), [today]);

  // Relative day for today ±1 — drives the label word and the language-neutral
  // `data-relative` hook; otherwise the label is "Sat 27 Sep" in the UI language.
  const relativeDay = useMemo<RelativeDay | null>(() => {
    if (value === today) return 'today';
    if (value === shiftIsoDate(today, -1)) return 'yesterday';
    if (value === shiftIsoDate(today, 1)) return 'tomorrow';
    return null;
  }, [today, value]);
  const relativeLabel = relativeDay ? t(`league:${relativeDay}`) : null;

  const label = useMemo(() => {
    if (relativeLabel) return relativeLabel;
    const parts = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).formatToParts(valueDate);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find(p => p.type === type)?.value ?? '';
    return `${part('weekday')} ${part('day')} ${part('month')}`;
  }, [locale, relativeLabel, valueDate]);

  const fullDate = useMemo(
    () => new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(valueDate),
    [locale, valueDate],
  );

  const close = () => setAnchorEl(null);

  const select = (iso: string) => {
    close();
    if (iso !== value) onChange(iso);
  };

  const calendar = (
    <div className={styles.calendar} data-testid={calendarTestIdPrefix}>
      {isSheet && <div className={styles.handle} aria-hidden="true" />}
      <MonthCalendar
        value={valueDate}
        today={todayDate}
        onSelect={date => select(toLocalIsoDate(date))}
        locale={locale}
        autoFocus
        testIdPrefix={calendarTestIdPrefix}
      />
      {value !== today && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.todayBtn}
            onClick={() => select(today)}
            data-testid={`${calendarTestIdPrefix}-today`}
          >
            {t('league:today')}
          </button>
        </div>
      )}
    </div>
  );

  const paperA11y = {
    role: 'dialog',
    'aria-modal': true,
    'aria-label': t('league:calendar'),
  } as const;

  return (
    <div className={styles.pager}>
      <CircleIconButton
        icon="chevron-left"
        ariaLabel={t('league:previousDay')}
        onClick={() => onChange(shiftIsoDate(value, -1))}
        shape="square"
        testId={`${testIdPrefix}-prev`}
      />

      <button
        type="button"
        className={styles.label}
        data-relative={relativeDay ?? undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('league:chooseDate', {
          date: relativeLabel ? `${relativeLabel}, ${fullDate}` : fullDate,
        })}
        onClick={event => setAnchorEl(event.currentTarget)}
        data-testid={`${testIdPrefix}-label`}
      >
        <span className={styles.labelText}>{label}</span>
        <i
          className={`icon-caret-down ${styles.caret}${open ? ` ${styles.caretOpen}` : ''}`}
          aria-hidden="true"
        />
      </button>

      <CircleIconButton
        icon="chevron-right"
        ariaLabel={t('league:nextDay')}
        onClick={() => onChange(shiftIsoDate(value, 1))}
        shape="square"
        testId={`${testIdPrefix}-next`}
      />

      {isSheet ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={close}
          onOpen={noop}
          disableSwipeToOpen
          // SwipeableDrawer keeps its content mounted by default (needed only
          // for swipe-to-open); remounting resets the calendar to the selected
          // month on every open.
          ModalProps={{ keepMounted: false }}
          slotProps={{ paper: { className: styles.sheet, ...paperA11y } }}
        >
          {calendar}
        </SwipeableDrawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{ paper: { className: styles.popover, ...paperA11y } }}
        >
          {calendar}
        </Popover>
      )}
    </div>
  );
};

export default DatePager;
